import axios from "axios";
import { ChevronDown, ChevronUp, Star } from "lucide-react";
import { useState } from "react";
import { useCart } from "../../../context/CartContext";
import ServiceDetailsOverlay from "./ServiceDetailsOverlay";

const SubCategoryCard = ({ subCategory, onAdd }) => {
  const subCategoryId = subCategory.subCategoryId;

  const [expandedMap, setExpandedMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});
  const [servicesBySubCategory, setServicesBySubCategory] = useState({});

  const isOpen = expandedMap[subCategoryId];
  const isLoading = loadingMap[subCategoryId];
  const services = servicesBySubCategory[subCategoryId] || [];

  const fetchServicesBySubCategory = async () => {
    // cache hit
    if (servicesBySubCategory[subCategoryId]) return;

    setLoadingMap((prev) => ({ ...prev, [subCategoryId]: true }));

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/get-all`,
        { params: { subCategoryId } },
      );

      if (res.data?.success) {
        setServicesBySubCategory((prev) => ({
          ...prev,
          [subCategoryId]: res.data.data,
        }));
      }
    } catch (err) {
      console.error("Service fetch failed", err);
    } finally {
      setLoadingMap((prev) => ({ ...prev, [subCategoryId]: false }));
    }
  };

  const handleToggle = async () => {
    setExpandedMap((prev) => ({
      ...prev,
      [subCategoryId]: !prev[subCategoryId],
    }));

    if (!servicesBySubCategory[subCategoryId]) {
      fetchServicesBySubCategory();
    }
  };

  return (
    <div className="">
      <div className="p-4 lg:p-5 bg-white rounded-xl shadow-lg  ">
        {/* HEADER */}
        <div
          className="flex items-center gap-3 xl:gap-4 cursor-pointer w-full "
          onClick={handleToggle}
        >
          <img
            src={subCategory.imageUrl}
            alt={subCategory.subCategoryName}
            className="size-[5.5rem] sm:size-24 md:size-28 lg:size-32 xl:size-36  rounded-lg object-center mx-auto"
          />

          <div className="flex flex-1 flex-col lg:flex-row lg:justify-between gap-2 sm:py-1">
            <div className="flex flex-1 grow w-full flex-col gap-y-1 md:gap-y-1.5 lg:gap-y-2">
              <span className="rounded-lg bg-gray-200 line-clamp-1 text-[0.65rem] sm:text-xs md:text-xs lg:text-sm text-gray-500 font-robotoflex font-medium sm:py-0.5 px-2 sm:px-4 w-fit">
                {subCategory.categoryName}
              </span>
              <h2 className="uppercase text-sm sm:text-base xl:text-xl line-clamp-1 font-semibold tracking-wide font-philosopher text-primaryTextColor">
                {subCategory.subCategoryName}
              </h2>
              <p className="uppercase text-[0.65rem] tracking-wide line-clamp-1 sm:text-sm xl:text-md text-secondaryTextColor font-robotoflex">
                {subCategory.description}
              </p>
            </div>
            <div className="text-accentTextColor flex gap-2 lg:flex-col justify-between lg:self-end ">
              <span className="flex text-xs sm:text-sm xl:text-base whitespace-normal md:whitespace-nowrap font-robotoflex items-center">
                <div className="flex items-center gap-1  text-[#EA875C]">
                  <span className="text-xs sm:text-sm xl:text-base">
                    {subCategory?.totalServices} Services Available
                  </span>
                  {isOpen ? (
                    <ChevronUp className="size-5" />
                  ) : (
                    <ChevronDown className="size-5" />
                  )}
                </div>
              </span>
            </div>
          </div>
        </div>

        {/* SERVICES */}
        {isOpen && (
          <div className="mt-4">
            {isLoading && (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4 py-3 items-start ">
                {Array.from({ length: 8 }).map((_, i) => (
                  <ServiceCardSkeleton key={i} />
                ))}
              </div>
            )}

            {!isLoading && services.length === 0 && (
              <div className="text-center py-10 text-gray-400">
                <p className="text-sm font-medium">No services found</p>
                <p className="text-xs mt-1">
                  Please check back later or explore another category.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4 py-3 items-start">
              {services.map((service) => (
                <ServiceCard
                  key={service._id}
                  service={service}
                  onAdd={onAdd}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default SubCategoryCard;

const ServiceCard = ({ service }) => {
  const { cart, addService } = useCart();

  const {
    _id,
    imageUrl,
    name,
    originalPrice,
    discountedPrice,
    type = [],
    ratings,
  } = service;

  const isVariantBased = Array.isArray(type) && type.length > 0;

  //  Single cart item per service
  const cartItem = cart.find((i) => i.serviceId === _id);

  const [openDetails, setOpenDetails] = useState(false);

  //  Selected variant (derived once, no circular refs)
  const [selectedVariant, setSelectedVariant] = useState(() => {
    if (!isVariantBased) return null;

    // restore from cart if already added
    if (cartItem?.brand) {
      return type.find((v) => v.brand === cartItem.brand) ?? null;
    }

    return null;
  });

  //  Qty is derived, NOT state
  const qty = cartItem?.qty ?? 0;

  const variants = isVariantBased ? type.slice(0, 2) : [];

  const increment = () => {
    let variantToUse = selectedVariant;

    //  Auto-pick first variant if none selected
    if (isVariantBased && !variantToUse) {
      variantToUse = type[0];
      setSelectedVariant(variantToUse);
    }

    addService({
      serviceId: _id,
      title: name,
      imageUrl,
      brand: variantToUse?.brand ?? null,
      originalPrice: variantToUse ? variantToUse.originalPrice : originalPrice,
      discountedPrice: variantToUse
        ? variantToUse.discountedPrice
        : discountedPrice,
      qty: qty + 1,
    });
  };

  const decrement = () => {
    addService({
      serviceId: _id,
      title: name,
      imageUrl,
      brand: selectedVariant?.brand ?? null,
      originalPrice: selectedVariant
        ? selectedVariant.originalPrice
        : originalPrice,
      discountedPrice: selectedVariant
        ? selectedVariant.discountedPrice
        : discountedPrice,
      qty: Math.max(qty - 1, 0),
    });
  };

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);

    if (qty > 0) {
      addService({
        serviceId: _id,
        title: name,
        imageUrl,
        brand: variant.brand,
        originalPrice: variant.originalPrice,
        discountedPrice: variant.discountedPrice,
        qty,
      });
    }
  };

  const formatPrice = (value) => {
    if (typeof value !== "number" || Number.isNaN(value)) return "0";
    return Math.round(value);
  };
  return (
    <div className="border rounded-lg bg-white shadow-lg overflow-hidden flex flex-col h-full">
      {/* IMAGE */}
      <div
        className="relative cursor-pointer"
        onClick={() => setOpenDetails(true)}
      >
        {" "}
        <img
          src={imageUrl}
          alt={name}
          className="w-full h-32 sm:h-48 object-cover"
        />
        {/* FLOATING RATING */}
        {ratings > 0 && (
          <div
            className="
        absolute top-2 right-2
        flex items-center gap-1
        px-2 py-1
        rounded-full
        bg-white/80 backdrop-blur
        shadow-sm
        text-xs font-semibold
        text-gray-800
      "
          >
            <Star size={12} className="text-yellow-500 fill-yellow-500" />
            {ratings}
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div className="p-2.5 sm:p-4 flex flex-col gap-y-2.5 flex-1">
        {/* TITLE */}
        <h3
          onClick={() => setOpenDetails(true)}
          className="font-semibold cursor-pointer text-sm sm:text-base line-clamp-1 text-primaryTextColor text-center"
        >
          {name}
        </h3>

        {/* VARIANTS */}
        {isVariantBased ? (
          <div className="grid grid-cols-2 gap-1.5 sm:gap-3">
            {variants.map((variant) => (
              <button
                key={variant.brand}
                onClick={() => handleVariantSelect(variant)}
                className={`p-1 rounded-md border text-xs sm:text-sm font-medium transition
                ${
                  selectedVariant?.brand === variant.brand
                    ? "border-accentTextColor shadow-sm"
                    : "border-gray-300 text-gray-600"
                }`}
              >
                <span className="line-clamp-1">{variant?.brand}</span>
                <div className="">
                  <div className="font-semibold text-accentTextColor">
                    ₹{formatPrice(variant.discountedPrice)}/-
                  </div>

                  {variant.discountedPrice < variant.originalPrice && (
                    <div className="text-[0.69rem] -mt-1 line-through text-gray-400">
                      ₹{formatPrice(variant.originalPrice)}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center">
            <div className="font-semibold text-accentTextColor">
              ₹{formatPrice(discountedPrice)}/-
            </div>{" "}
            {discountedPrice < originalPrice && (
              <div className="text-[0.69rem] -mt-1 line-through text-gray-400">
                ₹{formatPrice(originalPrice)}
              </div>
            )}
          </div>
        )}

        {/* ACTION SLOT (FIXED HEIGHT) */}
        <div className="mt-auto h-10">
          {qty === 0 ? (
            <button
              onClick={increment}
              className="w-full h-full text-sm sm:text-base bg-gray-200 hover:bg-gray-300 text-primaryTextColor font-medium rounded-lg transition"
            >
              Add-on
            </button>
          ) : (
            <div className="grid grid-cols-3 gap-1 h-full">
              <button
                onClick={decrement}
                className="active:scale-[0.98] text-xl sm:text-2xl font-bold bg-accentTextColor rounded-md text-white"
              >
                −
              </button>

              <span className="flex items-center justify-center text-sm sm:text-base rounded-md border border-gray-300 font-semibold bg-white">
                {qty}
              </span>

              <button
                onClick={increment}
                className="active:scale-[0.98] text-xl sm:text-2xl font-bold bg-accentTextColor rounded-md text-white"
              >
                +
              </button>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpenDetails(true)}
          className="text-accentTextColor font-semibold
          text-xs sm:text-sm
          text-center
          hover:underline
  "
        >
          View details
        </button>
      </div>
      <ServiceDetailsOverlay
        service={service}
        open={openDetails}
        onClose={() => setOpenDetails(false)}
      />
    </div>
  );
};

const ServiceCardSkeleton = () => {
  return (
    <div className="border rounded-lg bg-white shadow-sm overflow-hidden flex flex-col h-full animate-pulse">
      {/* Image */}
      <div className="w-full h-32 sm:h-48 bg-gray-200" />

      {/* Content */}
      <div className="p-2.5 sm:p-4 flex flex-col gap-y-2.5 flex-1">
        {/* Title */}
        <div className="h-4 sm:h-5 bg-gray-200 rounded w-3/4 mx-auto" />

        {/* Price / Variants */}
        <div className="grid grid-cols-2 gap-1.5 sm:gap-3">
          <div className="h-10 bg-gray-200 rounded-md" />
          <div className="h-10 bg-gray-200 rounded-md" />
        </div>

        {/* Action Slot */}
        <div className="mt-auto h-10 bg-gray-200 rounded-lg" />
      </div>
    </div>
  );
};
